import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import Waveform from "@/components/Waveform";
import Watermark from "@/components/Watermark";

const BG = "https://images.unsplash.com/photo-1758506971986-b0d0edebd8d5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const line = { hidden: { y: "110%" }, show: (i) => ({ y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.15 } }) };

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [launchDate, setLaunchDate] = useState(null);
  const [left, setLeft] = useState(null);

  useEffect(() => {
    api.get("/site/status").then((r) => setLaunchDate(r.data.launch_date || null)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!launchDate) return;
    const target = new Date(launchDate).getTime();
    const tick = () => {
      const d = Math.max(0, target - Date.now());
      setLeft({
        days: Math.floor(d / 86400000),
        hours: Math.floor(d / 3600000) % 24,
        minutes: Math.floor(d / 60000) % 60,
        seconds: Math.floor(d / 1000) % 60,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [launchDate]);

  const join = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/launch/notify", { email });
      setCode(data.code);
      setJoined(true);
    } catch {
      toast.error("Could not sign you up. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen bg-ink flex flex-col overflow-hidden" data-testid="coming-soon-page">
      <div className="fixed inset-0">
        <img src={BG} alt="Runner in motion" className="w-full h-full object-cover grayscale opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
      </div>
      <Watermark />

      <header className="relative z-10 container-sync flex items-center justify-between h-24">
        <Logo variant="full" size="md" asLink={false} />
        <Link to="/story" data-testid="coming-soon-story-link"
          className="font-mono text-xs uppercase tracking-[0.2em] text-chalk/80 hover:text-pulse link-underline">
          Our Story →
        </Link>
      </header>

      <main className="relative z-10 flex-1 flex items-center">
        <div className="container-sync w-full py-16">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
            className="eyebrow mb-8 !text-signal">Official launch — Coming Soon</motion.p>
          <h1 className="font-anton uppercase leading-[0.85] tracking-tight text-[15vw] sm:text-[12vw] lg:text-[8.5vw] text-chalk">
            {["Move With", "The Beat"].map((l, i) => (
              <span key={l} className="block overflow-hidden">
                <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">{l}</motion.span>
              </span>
            ))}
          </h1>
          <Waveform color="rgb(var(--graphite))" className="my-10 max-w-xl" />
          {left && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }}
              className="mb-10" data-testid="launch-countdown">
              <div className="flex flex-wrap gap-3 sm:gap-4">
                {[["Days", left.days], ["Hours", left.hours], ["Min", left.minutes], ["Sec", left.seconds]].map(([label, v]) => (
                  <div key={label} className="border border-border bg-ink/60 backdrop-blur-md px-4 sm:px-6 py-4 min-w-[76px] sm:min-w-[96px] text-center">
                    <p className="font-anton text-3xl sm:text-5xl text-chalk tabular-nums" data-testid={`countdown-${label.toLowerCase()}`}>
                      {String(v).padStart(2, "0")}
                    </p>
                    <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-graphite mt-2">{label}</p>
                  </div>
                ))}
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-signal mt-4" data-testid="launch-date-label">
                Doors open {new Date(launchDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </p>
            </motion.div>
          )}
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.8 }}
            className="text-graphite text-lg max-w-xl leading-[1.85]">
            Fitness · Running · Music · Streetwear. Minimal apparel engineered for the rhythm of
            your life — dropping soon. Limited numbers. One release.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1 }}
            className="mt-12 max-w-md">
            {joined ? (
              <div data-testid="launch-notify-success">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-signal">
                  You're first in line — we'll email you at launch.
                </p>
                <div className="mt-5 inline-block border border-dashed border-signal px-8 py-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-graphite mb-1">Your 10% launch code</p>
                  <p className="font-anton text-2xl tracking-[0.25em] text-signal" data-testid="launch-code">{code}</p>
                </div>
                <p className="text-graphite text-xs mt-4 font-mono">Also sent to your inbox — valid the day we open.</p>
              </div>
            ) : (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-chalk/80 mb-4">
                  Be first in — get an instant <span className="text-signal">10% off launch code</span>
                </p>
                <form onSubmit={join} className="flex gap-3" data-testid="launch-notify-form">
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com" data-testid="launch-notify-email"
                    className="flex-1 bg-transparent border-b border-border py-3 font-mono text-sm focus:border-signal outline-none" />
                  <button type="submit" disabled={loading} data-testid="launch-notify-submit"
                    className="font-mono text-[11px] uppercase tracking-wider bg-signal text-ink px-6 hover:bg-signal/85 transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                    {loading ? "…" : <>Notify Me <ArrowRight size={14} /></>}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </main>

      <div className="relative z-10 container-sync pb-6 flex flex-wrap gap-x-10 gap-y-2">
        <a href="mailto:info@thesyncwear.com" data-testid="coming-soon-contact"
          className="font-mono text-xs uppercase tracking-[0.2em] text-graphite hover:text-pulse transition-colors">
          Contact — info@thesyncwear.com
        </a>
        <a href="https://www.instagram.com/itssyncwear" target="_blank" rel="noopener noreferrer" data-testid="coming-soon-instagram"
          className="font-mono text-xs uppercase tracking-[0.2em] text-graphite hover:text-pulse transition-colors">
          Instagram — @itssyncwear
        </a>
      </div>
      <div className="relative z-10 border-t border-border/60 py-6">
        <Marquee speed={50} gradient={false}>
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="font-mono text-xs uppercase tracking-[0.35em] text-graphite mx-10">
              Coming Soon <span className="text-pulse mx-3">●</span> Move With The Beat
            </span>
          ))}
        </Marquee>
      </div>
    </div>
  );
}
